import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight, CheckCircle, FileText } from 'lucide-react';
import { WHATSAPP_LINK } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Quote = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',

    // Step 2: Property Info
    propertyType: '',
    roofType: '',
    availableArea: '',
    hasShade: '',

    // Step 3: Energy Info
    monthlyBill: '',
    currentProvider: '',
    hasThreePhase: '',

    // Step 4: Additional
    installationUrgency: '',
    financingInterest: '',
    additionalNotes: '',
    acceptTerms: false
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleNext = () => {
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.acceptTerms) {
      toast({
        title: "Aceite os termos",
        description: "Por favor, aceite os termos e condições para continuar.",
        variant: "destructive"
      });
      return;
    }

    // Format comprehensive message for WhatsApp
    const message = encodeURIComponent(
      `🌞 SOLICITAÇÃO DE ORÇAMENTO - W3SOLAR\n\n` +
      `👤 DADOS PESSOAIS\n` +
      `Nome: ${formData.name}\n` +
      `E-mail: ${formData.email}\n` +
      `Telefone: ${formData.phone}\n` +
      `Cidade/Estado: ${formData.city} - ${formData.state}\n\n` +
      `🏠 DADOS DO IMÓVEL\n` +
      `Tipo: ${formData.propertyType}\n` +
      `Tipo de Telhado: ${formData.roofType}\n` +
      `Área Disponível: ${formData.availableArea}m²\n` +
      `Tem Sombreamento: ${formData.hasShade}\n\n` +
      `⚡ DADOS ENERGÉTICOS\n` +
      `Conta Mensal: R$ ${formData.monthlyBill}\n` +
      `Concessionária: ${formData.currentProvider}\n` +
      `Trifásico: ${formData.hasThreePhase}\n\n` +
      `📋 INFORMAÇÕES ADICIONAIS\n` +
      `Urgência: ${formData.installationUrgency}\n` +
      `Interesse em Financiamento: ${formData.financingInterest}\n` +
      `Observações: ${formData.additionalNotes || 'Nenhuma'}\n\n` +
      `Aguardo retorno com orçamento detalhado!`
    );

    window.open(`${WHATSAPP_LINK}?text=${message}`, '_blank');

    toast({
      title: "Orçamento enviado!",
      description: "Redirecionando para WhatsApp. Aguarde nosso contato em breve!",
    });
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 mx-1 rounded-full transition-all ${
              s <= step ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-sm text-gray-600">Etapa {step} de 4</p>
    </div>
  );

  return (
    <div className="min-h-screen pt-16">
      <SEO 
        title="Solicitar Orçamento - W3Solar | Orçamento Grátis de Energia Solar"
        description="Solicite seu orçamento personalizado de energia solar. Preencha o formulário e receba uma proposta completa sem compromisso."
        keywords="orçamento energia solar, solicitar orçamento painéis solares, proposta energia solar grátis"
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <FileText className="h-16 w-16 text-emerald-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Solicitar Orçamento
            </h1>
            <p className="text-xl text-gray-600">
              Preencha o formulário completo para receber um orçamento personalizado
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-2xl">
                {step === 1 && 'Dados Pessoais'}
                {step === 2 && 'Informações do Imóvel'}
                {step === 3 && 'Consumo de Energia'}
                {step === 4 && 'Informações Finais'}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Informe seus dados de contato'}
                {step === 2 && 'Detalhes sobre a propriedade'}
                {step === 3 && 'Informações sobre seu consumo atual'}
                {step === 4 && 'Últimos detalhes para seu orçamento'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderProgressBar()}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Personal Info */}
                {step === 1 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome Completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Seu nome completo"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu@email.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone/WhatsApp *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(11) 99999-9999"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">Cidade *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="São Paulo"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">Estado *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="SP"
                          maxLength="2"
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    >
                      Próxima Etapa
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </>
                )}

                {/* Step 2: Property Info */}
                {step === 2 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="propertyType">Tipo de Imóvel *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('propertyType', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Casa">Casa</SelectItem>
                          <SelectItem value="Apartamento">Apartamento</SelectItem>
                          <SelectItem value="Comércio">Comércio</SelectItem>
                          <SelectItem value="Indústria">Indústria</SelectItem>
                          <SelectItem value="Fazenda/Rural">Fazenda/Rural</SelectItem>
                          <SelectItem value="Condomínio">Condomínio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="roofType">Tipo de Telhado *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('roofType', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cerâmica">Cerâmica</SelectItem>
                          <SelectItem value="Metálico">Metálico</SelectItem>
                          <SelectItem value="Laje">Laje</SelectItem>
                          <SelectItem value="Fibrocimento">Fibrocimento</SelectItem>
                          <SelectItem value="Outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="availableArea">Área Disponível Estimada (m²) *</Label>
                      <Input
                        id="availableArea"
                        name="availableArea"
                        type="number"
                        value={formData.availableArea}
                        onChange={handleChange}
                        placeholder="50"
                        required
                      />
                      <p className="text-sm text-gray-500">
                        Área aproximada do telhado ou local para instalação
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hasShade">O local tem sombreamento? *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('hasShade', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Não">Não</SelectItem>
                          <SelectItem value="Pouco (manhã ou tarde)">Pouco (manhã ou tarde)</SelectItem>
                          <SelectItem value="Moderado">Moderado</SelectItem>
                          <SelectItem value="Muito">Muito</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-3">
                      <Button type="button" onClick={handleBack} variant="outline" className="flex-1">
                        Voltar
                      </Button>
                      <Button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                      >
                        Próxima Etapa
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 3: Energy Info */}
                {step === 3 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="monthlyBill">Valor Médio da Conta de Luz (R$) *</Label>
                      <Input
                        id="monthlyBill"
                        name="monthlyBill"
                        type="number"
                        value={formData.monthlyBill}
                        onChange={handleChange}
                        placeholder="500"
                        required
                      />
                      <p className="text-sm text-gray-500">
                        Informe o valor médio dos últimos 3 meses
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currentProvider">Concessionária de Energia *</Label>
                      <Input
                        id="currentProvider"
                        name="currentProvider"
                        value={formData.currentProvider}
                        onChange={handleChange}
                        placeholder="Ex: CPFL, Enel, Elektro"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hasThreePhase">Tipo de Ligação *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('hasThreePhase', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monofásica (127V)">Monofásica (127V)</SelectItem>
                          <SelectItem value="Bifásica (220V)">Bifásica (220V)</SelectItem>
                          <SelectItem value="Trifásica (380V)">Trifásica (380V)</SelectItem>
                          <SelectItem value="Não sei">Não sei</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-3">
                      <Button type="button" onClick={handleBack} variant="outline" className="flex-1">
                        Voltar
                      </Button>
                      <Button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                      >
                        Próxima Etapa
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 4: Additional Info */}
                {step === 4 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="installationUrgency">Prazo Desejado para Instalação *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('installationUrgency', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Urgente (até 30 dias)">Urgente (até 30 dias)</SelectItem>
                          <SelectItem value="Médio prazo (1-3 meses)">Médio prazo (1-3 meses)</SelectItem>
                          <SelectItem value="Longo prazo (3-6 meses)">Longo prazo (3-6 meses)</SelectItem>
                          <SelectItem value="Apenas pesquisando">Apenas pesquisando</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="financingInterest">Tem interesse em financiamento? *</Label>
                      <Select
                        onValueChange={(value) => handleSelectChange('financingInterest', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sim, tenho interesse">Sim, tenho interesse</SelectItem>
                          <SelectItem value="Talvez, quero saber mais">Talvez, quero saber mais</SelectItem>
                          <SelectItem value="Não, pagamento à vista">Não, pagamento à vista</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionalNotes">Observações ou Dúvidas (opcional)</Label>
                      <Textarea
                        id="additionalNotes"
                        name="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={handleChange}
                        placeholder="Alguma informação adicional que gostaria de compartilhar..."
                        rows={4}
                      />
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg">
                      <Checkbox
                        id="acceptTerms"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, acceptTerms: checked })
                        }
                      />
                      <label
                        htmlFor="acceptTerms"
                        className="text-sm text-gray-700 cursor-pointer leading-relaxed"
                      >
                        Aceito receber contato da W3Solar via WhatsApp, e-mail ou telefone para apresentação do orçamento e esclarecimento de dúvidas. *
                      </label>
                    </div>

                    <div className="flex gap-3">
                      <Button type="button" onClick={handleBack} variant="outline" className="flex-1">
                        Voltar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                      >
                        Enviar Orçamento
                        <CheckCircle className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Security Note */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-900">
                🔒 Seus dados estão seguros. Não compartilhamos suas informações com terceiros.
                Entraremos em contato exclusivamente para apresentar seu orçamento personalizado.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Quote;
